/**
 * Lineage Graph view — Faz 8.1.
 *
 * Visualises a `correlationId` as a vertical timeline with audit↔
 * actuator pairs highlighted by colored connectors. The graph is
 * intentionally text-first (no SVG/canvas) so it stays accessible,
 * pasteable into incident tickets, and free of layout-engine bugs;
 * the visual structure is the indentation + the pair badges.
 */

import { useMemo, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import { ApiError } from "../api/client.js";
import { fetchLineage, queryKeys } from "../api/queries.js";

export const LineageGraphView = (): ReactElement => {
  const params = useParams<{ correlationId: string }>();
  const correlationId = params.correlationId ?? "";

  const query = useQuery({
    queryKey: queryKeys.lineage(correlationId),
    queryFn: () => fetchLineage(correlationId),
    enabled: correlationId !== "",
  });

  const pairLookup = useMemo(() => {
    const m = new Map<string, { role: "audit" | "actuator"; action: string; otherTickId: string }>();
    for (const p of query.data?.pairs ?? []) {
      m.set(p.auditTickId, {
        role: "audit",
        action: p.action,
        otherTickId: p.actuatorTickId,
      });
      m.set(p.actuatorTickId, {
        role: "actuator",
        action: p.action,
        otherTickId: p.auditTickId,
      });
    }
    return m;
  }, [query.data]);

  return (
    <section className="space-y-4">
      <header className="flex items-center gap-3">
        <Link to="/events" className="text-sm text-sky-400 hover:underline">
          ← Back to timeline
        </Link>
        <h2 className="text-lg font-semibold">Lineage</h2>
        <span className="rounded bg-slate-800 px-2 py-1 text-xs font-mono">{correlationId}</span>
      </header>

      {query.isLoading && <p data-testid="loading">Loading…</p>}
      {query.isError && (
        <p className="text-rose-400" data-testid="error">
          {query.error instanceof ApiError
            ? `${query.error.server.kind}: ${query.error.server.message}`
            : "Unknown error"}
        </p>
      )}

      {query.data && (
        <>
          <p className="text-sm text-slate-400">
            {query.data.events.length} event(s) · {query.data.pairs.length} audit↔actuator pair(s)
          </p>
          <ol className="space-y-1 border-l-2 border-slate-700 pl-4">
            {query.data.events.map((row) => {
              const tickId = String(row.event.tickId);
              const pair = pairLookup.get(tickId);
              return (
                <li
                  key={tickId}
                  data-testid={`lineage-row-${tickId}`}
                  className="flex items-center gap-3 rounded px-2 py-1 hover:bg-slate-900/50"
                >
                  <span className="w-24 font-mono text-xs text-slate-400">{tickId}</span>
                  <Link
                    to={`/events/${tickId}`}
                    className="font-mono text-sm text-sky-400 hover:underline"
                  >
                    {row.event.kind}
                  </Link>
                  {pair && (
                    <span
                      className={`rounded px-2 py-0.5 text-xs font-semibold ${
                        pair.role === "audit"
                          ? "bg-amber-500/20 text-amber-300"
                          : "bg-emerald-500/20 text-emerald-300"
                      }`}
                      title={`paired with tickId=${pair.otherTickId}`}
                    >
                      {pair.role}: {pair.action}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </>
      )}
    </section>
  );
};
